import React from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { productsApi } from "../../../api/products";
import { ErpEmpty, ErpLoading, ErpPanel, ErpSectionTitle } from "../../../components/dashboard/erp/ErpUi";
import { ERP_BRAND, entityId, formatDateTime, formatMoney, projectProductCode, projectProductName } from "../../../utils/erp";

export default function ProjectErpStockPage() {
  const { projectId } = useParams();
  const [stockMovements, setStockMovements] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [filters, setFilters] = React.useState({ productId: "", dateFrom: "", dateTo: "" });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    productsApi.list(projectId).then((data) => setProducts(data.products || [])).catch(() => setProducts([]));
  }, [projectId]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.listStockMovements(projectId, { page: page + 1, pageSize: rowsPerPage, ...filters });
      setStockMovements(data.stockMovements || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message || "No se pudo cargar la trazabilidad de stock");
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage, filters]);

  React.useEffect(() => { load(); }, [load]);

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  return (
    <Stack spacing={2.4}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      <ErpSectionTitle title="Trazabilidad de stock" description="Historial inmutable de entradas, salidas, ajustes y reversas por producto." action={<Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} sx={{ textTransform: "none", fontWeight: 850 }}>Actualizar</Button>} />

      <ErpPanel>
        <Box sx={{ p: 2.2, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.3}>
            <TextField select size="small" label="Producto" value={filters.productId} onChange={(event) => setFilter("productId", event.target.value)} sx={{ minWidth: 320 }}>
              <MenuItem value="">Todos los productos</MenuItem>
              {products.map((product) => <MenuItem key={entityId(product)} value={entityId(product)}>{projectProductName(product)}{projectProductCode(product) ? ` · ${projectProductCode(product)}` : ""}</MenuItem>)}
            </TextField>
            <TextField size="small" type="date" label="Desde" value={filters.dateFrom} onChange={(event) => setFilter("dateFrom", event.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Hasta" value={filters.dateTo} onChange={(event) => setFilter("dateTo", event.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>
        </Box>
        {loading ? <ErpLoading label="Cargando movimientos de stock..." /> : !stockMovements.length ? <ErpEmpty title="Sin movimientos de stock" description="Las ventas, compras, entradas, salidas y ajustes confirmados aparecerán aquí." /> : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="right">Stock anterior</TableCell>
                    <TableCell align="right">Variación</TableCell>
                    <TableCell align="right">Stock posterior</TableCell>
                    <TableCell align="right">Costo unitario</TableCell>
                    <TableCell>Usuario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockMovements.map((movement) => (
                    <TableRow key={entityId(movement)} hover>
                      <TableCell>{formatDateTime(movement.occurredAt)}</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>{movement.productNameSnapshot}</TableCell>
                      <TableCell>{movement.kind === "in" ? "Entrada" : movement.kind === "out" ? "Salida" : movement.kind === "adjustment" ? "Ajuste" : "Reversa"}</TableCell>
                      <TableCell align="right">{movement.stockBefore}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 950, color: Number(movement.quantityDelta) >= 0 ? "success.main" : "error.main" }}>{Number(movement.quantityDelta) > 0 ? `+${movement.quantityDelta}` : movement.quantityDelta}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900 }}>{movement.stockAfter}</TableCell>
                      <TableCell align="right">{formatMoney(movement.unitCost)}</TableCell>
                      <TableCell>{movement.createdByName || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={total} page={page} onPageChange={(_event, nextPage) => setPage(nextPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Filas por página" />
          </>
        )}
      </ErpPanel>
    </Stack>
  );
}
